import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {BreadCrumb} from "primereact/breadcrumb";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {Paginator} from "primereact/paginator";
import Icon from "../../system/components/icon";
import PageHeaderButton from "../components/page-header-button";
import PrimeDatePicker from "../../system/components/prime-date-picker";
import {useTranslate} from "../../system/useTranslate";
import {workspaceGraphSelectors} from "../../system/workspace-graph-slice";
import useSearch from "../../system/components/global-search/use-search";
import SearchResultItem from "../../system/components/global-search/search-result-item";
import {SEARCH_MIN_LENGTH, SEARCH_TYPES, STATUS_FILTERS, resultTarget, searchTypeMeta} from "../../system/components/global-search/search-config";
import "../../system/components/global-search/global-search.css";

// Full search results (the «مشاهده تمام نتایج» link of the header search; docs/tasks/21-global-search.md): one kind per
// tab with paging, or every kind with its first 10, and filters for date, person and status. The state lives in the
// URL (?q=&type=&person=&status=&from=&to=&page=) so a search can be shared or reopened.

const PAGE_SIZE = 20;
const ALL_PREVIEW = 10;

const memberName = (membership) => {
    const member = membership?.member || {};
    return member.fullname || [member.firstname, member.lastname].filter(Boolean).join(" ") || member.username || "";
};
const unixToIso = (value, endOfDay = false) => {
    if (!value) return null;
    const date = new Date(Number(value) * 1000);
    if (endOfDay) date.setHours(23, 59, 59, 999);
    else date.setHours(0, 0, 0, 0);
    return date.toISOString();
};
const isoToUnix = (value) => (value ? Math.floor(new Date(value).getTime() / 1000) : null);

function SearchPage(props) {
    const translate = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const memberships = useSelector(workspaceGraphSelectors.workspaceMemberships);
    const urlQuery = searchParams.get("q") || "";
    const [text, setText] = useState(urlQuery);

    const type = SEARCH_TYPES.some((item) => item.type === searchParams.get("type")) ? searchParams.get("type") : "all";
    const page = Math.max(1, Number(searchParams.get("page")) || 1);
    const filters = {
        person: searchParams.get("person") || "",
        status: searchParams.get("status") || "",
        from: searchParams.get("from") || "",
        to: searchParams.get("to") || "",
    };

    useEffect(() => {
        setText(urlQuery);
    }, [urlQuery]);

    const update = (changes, resetPage = true) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "" || (key === "type" && value === "all")) next.delete(key);
            else next.set(key, value);
        });
        if (resetPage && !("page" in changes)) next.delete("page");
        setSearchParams(next);
    };

    // typing updates the address after the same short pause as the request
    useEffect(() => {
        if (text.trim() === urlQuery.trim()) return undefined;
        const timer = setTimeout(() => update({q: text.trim()}), 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text]);

    const {loading, error, data} = useSearch({
        q: urlQuery,
        type,
        page: type === "all" ? 1 : page,
        pageSize: type === "all" ? ALL_PREVIEW : PAGE_SIZE,
        person: filters.person,
        status: filters.status,
        from: filters.from,
        to: filters.to,
    }, {delay: 0});
    // the counts of the tabs come from a light «all» request with the same filters
    const counts = useSearch({q: urlQuery, pageSize: 1, person: filters.person, status: filters.status, from: filters.from, to: filters.to}, {delay: 0});
    const countOf = (kind) => (kind === "all"
        ? counts.data?.total
        : counts.data?.groups?.find((group) => group.type === kind)?.total);

    const groups = useMemo(() => SEARCH_TYPES
        .map((meta) => (data?.groups || []).find((group) => group.type === meta.type))
        .filter((group) => group && (type !== "all" || group.items?.length)), [data, type]);

    const personOptions = useMemo(() => (memberships || [])
        .filter((membership) => membership?.documentId && !["removed"].includes(membership.memberShipStatus))
        .map((membership) => ({value: membership.documentId, label: memberName(membership)}))
        .filter((option) => option.label)
        .sort((a, b) => a.label.localeCompare(b.label, "fa")), [memberships]);
    const statusOptions = STATUS_FILTERS
        .filter((option) => type === "all" || option.types.includes(type))
        .map((option, index) => ({...option, key: `${option.value}-${index}`}));
    const hasFilters = Object.values(filters).some(Boolean);

    const openItem = (item) => {
        const target = resultTarget(item, location);
        if (target) navigate(target);
    };

    const breadCrumb = [{label: translate("search"), command: () => navigate("/search")}];

    return (
        <div className="h-full overflow-auto flex flex-column">
            <BreadCrumb separatorIcon={<Icon icon="mi mi:chevron_left text-xs"/>} className="bg-transparent border-none text-xs" model={breadCrumb}/>
            <PageHeaderButton title={props.name || "search"} description={props.description || "search.description"}/>

            <div className="p-3 flex flex-column gap-3">
                <div className="global-search-field">
                    <Icon icon="mi mi:search" className="global-search-field-icon"/>
                    <input
                        className="global-search-input h-3rem text-base"
                        value={text}
                        autoFocus={!urlQuery}
                        placeholder={translate("search.placeholder")}
                        aria-label={translate("search.placeholder")}
                        onChange={(event) => setText(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") update({q: text.trim()});
                        }}
                    />
                    {loading && <i className="pi pi-spin pi-spinner global-search-shortcut text-500"/>}
                </div>

                <div className="flex flex-wrap align-items-center gap-2">
                    <Dropdown
                        className="text-xs w-12rem"
                        panelClassName="rtl text-xs"
                        value={filters.person || null}
                        options={personOptions}
                        filter
                        showClear
                        placeholder={translate("search.filter.person")}
                        emptyMessage={translate("search.filter.no-person")}
                        onChange={(event) => update({person: event.value || ""})}
                    />
                    {type !== "message" && type !== "team" && type !== "customer" && (
                        <Dropdown
                            className="text-xs w-14rem"
                            panelClassName="rtl text-xs"
                            value={statusOptions.find((option) => option.value === filters.status)?.key || null}
                            options={statusOptions}
                            optionValue="key"
                            showClear
                            placeholder={translate("search.filter.status")}
                            onChange={(event) => update({status: statusOptions.find((option) => option.key === event.value)?.value || ""})}
                        />
                    )}
                    <PrimeDatePicker
                        type="PERSIAN_DATE"
                        inputClassName="text-xs w-10rem"
                        placeholder={translate("search.filter.from")}
                        value={isoToUnix(filters.from)}
                        onChange={(event) => update({from: unixToIso(event.value)})}
                    />
                    <PrimeDatePicker
                        type="PERSIAN_DATE"
                        inputClassName="text-xs w-10rem"
                        placeholder={translate("search.filter.to")}
                        value={isoToUnix(filters.to)}
                        onChange={(event) => update({to: unixToIso(event.value, true)})}
                    />
                    {hasFilters && (
                        <Button
                            text
                            severity="secondary"
                            size="small"
                            className="text-xs gap-2"
                            icon={<Icon icon="mi mi:filter_alt_off" className="text-base"/>}
                            label={translate("search.filter.clear")}
                            onClick={() => update({person: "", status: "", from: "", to: ""})}
                        />
                    )}
                </div>
                {hasFilters && (
                    <span className="text-xs text-500">{translate("search.filter.hint")}</span>
                )}

                <div className="search-page-tabs">
                    {[{type: "all", label: "search.type.all"}, ...SEARCH_TYPES].map((item) => {
                        const count = countOf(item.type);
                        return (
                            <button key={item.type} type="button" className={`global-search-tab ${type === item.type ? "is-active" : ""}`}
                                onClick={() => update({type: item.type, status: item.type === "all" || STATUS_FILTERS.some((option) => option.value === filters.status && option.types.includes(item.type)) ? filters.status : ""})}>
                                {translate(item.label)}
                                {!!count && <span className="global-search-tab-count">{count}</span>}
                            </button>
                        );
                    })}
                </div>

                <div className="search-page-results flex flex-column gap-3">
                    {urlQuery.trim().length < SEARCH_MIN_LENGTH ? (
                        <div className="global-search-empty">{translate("search.page.start")}</div>
                    ) : error ? (
                        <div className="global-search-empty">{translate("search.error")}</div>
                    ) : !data ? (
                        <div className="global-search-empty"><i className="pi pi-spin pi-spinner"/></div>
                    ) : !groups.some((group) => group.items?.length || group.unavailable) ? (
                        <div className="global-search-empty">{translate("search.empty").replace("{query}", urlQuery.trim())}</div>
                    ) : groups.map((group) => (
                        <div key={group.type} className="surface-card border-1 border-100 border-round-xl p-2">
                            <div className="global-search-group-head">
                                <span className="text-sm">{translate(searchTypeMeta(group.type).group)}</span>
                                <span className="global-search-group-count">{group.total} {translate("search.results")}</span>
                            </div>
                            {group.unavailable && <div className="text-xs text-500 px-3 pb-2">{translate("search.customers-unavailable")}</div>}
                            {group.items.map((item) => (
                                <SearchResultItem key={`${item.type}-${item.id}`} item={item} translate={translate} detailed onSelect={openItem}/>
                            ))}
                            {type === "all" && group.total > group.items.length && (
                                <Button text size="small" className="text-xs w-full mt-1" label={translate("search.page.more").replace("{count}", group.total)}
                                    onClick={() => update({type: group.type})}/>
                            )}
                            {type !== "all" && group.total > PAGE_SIZE && (
                                <Paginator
                                    className="text-xs"
                                    first={(page - 1) * PAGE_SIZE}
                                    rows={PAGE_SIZE}
                                    totalRecords={group.total}
                                    onPageChange={(event) => update({page: event.page + 1 > 1 ? String(event.page + 1) : ""}, false)}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default SearchPage;
